/**
 * Migrations for a STORED table layout, wherever it is stored.
 *
 * THE HOLE THIS CLOSES — a column layout lives in TWO places, and until now
 * only one of them was ever migrated:
 *
 *   localStorage  `md.ui.prefs` -> uiPrefs.ts `loadPrefs()`, gated by a
 *                 `_version` ladder that has run eight migrations to date.
 *   server        `cp_table_layouts` (one row per user per table) ->
 *                 tableLayout.tsx. No version. No migration. And it OUTRANKS
 *                 localStorage in `seedLayout` — deliberately, so "my columns
 *                 follow me to another machine" is true.
 *
 * So every uiPrefs migration that edits visibility / order / widths / pins has
 * silently been half a migration: the local copy changes, the server copy
 * doesn't, and the higher-precedence layer then serves the pre-migration answer
 * back forever. It is not a bug in any one migration — it is the shape of all
 * of them.
 *
 * WHAT IT COST (owner, 2026-09-04: "the SQP table is flickering a lot too"):
 *
 * The v7 -> v8 migration (PR #342, ticket #28) made eleven Amazon Search Query
 * Performance columns visible by default, and — correctly — deleted their
 * `visibility[id] = false` entries from localStorage, because a stale explicit
 * `false` would outlive the default change. Six of those eleven were also in
 * the owner's SERVER row for `ba.sqp.merged.brand` (saved 2026-09-03 18:25:
 * total_impressions, total_clicks, brand_clicks, total_cart_adds,
 * total_purchases, brand_purchases). That row was never migrated.
 *
 * The two layers then disagreed by exactly six columns, and the table has two
 * independent writers of `columnVisibility` — the mount seed / server-apply
 * effect reading the server layer, and the cross-instance sync effect reading
 * localStorage. Neither yields. His screen recording shows the column menu
 * alternating between "30 visible - 46 total" and "24 visible - 46 total"
 * about ten times a second. 30 - 24 = 6.
 *
 * The system is built to heal this by itself: the write-through effect PUTs the
 * resolved layout, which would end the disagreement after one page load. It
 * never fired. `saveUserLayout` debounces per table and clears its timer on
 * every call, so an oscillation faster than the debounce resets the timer
 * forever and the settling PUT is starved. Confirmed against prod: his row's
 * `updated_at` was still 2026-09-03 18:25 after the recording — thousands of
 * writes, zero PUTs. (That starvation is fixed separately, with a max-wait, in
 * tableLayout.tsx: a debounce that can be starved indefinitely is not a
 * debounce, it is a dropped write.)
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE: a migration must run on every layer
 * that stores the data it migrates. Put new layout migrations HERE, not in
 * uiPrefs' `_version` ladder, and they reach both.
 */

/** Bump when you add a rule below. Recorded per table id in the ledger.
 *  MERGE NOTE (PR 11): other Wave-4 PRs bump this too — whoever lands second
 *  renumbers their rule and this constant. */
export const STORED_LAYOUT_VERSION = 13

/**
 * The eleven Amazon SQP columns whose `defaultHidden: true` was removed in
 * PR #342 (ticket #28 — the owner's "the table is missing most of the SQP
 * datapoints", which stayed true after they were added because they existed
 * but were off). A stale explicit `false` for any of them outranks the new
 * default, so it has to go. Mirrors the AMAZON_SQP_COLS list in uiPrefs.ts's
 * v7 -> v8 block; the two must stay in step, and this comment is the only
 * thing keeping them honest — they are separate because uiPrefs owns the
 * localStorage schema and this file owns the layout shape.
 */
const TIMELINE_LAYOUT_KEYS = [
  'orders',
  'orders.table',
  'orders.section.v2.Received',
  'orders.section.v2.Customs',
  'orders.section.v2.In Transit',
  'orders.section.v2.Scheduled',
  'orders.section.v2.Ready',
  'orders.section.v2.Production',
  'orders.section.v2.Ordered',
]

/**
 * The eight Timeline packing columns from uiPrefs' v8 -> v9 block.
 *
 * THIS RULE IS OVERDUE. uiPrefs shipped v9 on its own, and the server row --
 * which OUTRANKS localStorage in `seedLayout` -- never got the matching rule,
 * so for anybody carrying a `cp_table_layouts` row with these hidden the
 * migration has been silently doing nothing since it shipped. That is exactly
 * the half-migration this module's header warns about; it is fixed here rather
 * than left as a known-broken rule.
 */
const TIMELINE_PACKING_COLS = [
  'cartons', 'units_per_carton', 'carton_length_cm', 'carton_width_cm',
  'carton_height_cm', 'carton_weight_kg', 'carton_cbm', 'line_cbm',
]

const AMAZON_SQP_COLS = [
  'total_impressions', 'total_clicks', 'total_click_rate', 'brand_clicks',
  'total_cart_adds', 'total_cart_add_rate', 'brand_cart_adds',
  'cart_add_share', 'total_purchases', 'total_purchase_rate',
  'brand_purchases',
]

/**
 * v10 -> v11 (#114, owner 2026-09-23: "purchase rate should be CVR. Purchases /
 * Clicks"). On the SQP tables `cvr` (market purchases / market clicks) becomes
 * visible by default and Amazon's `total_purchase_rate` (purchases / SEARCHES)
 * becomes hidden-by-default, kept for comparison. Both flips need their stale
 * explicit entry deleted: every defaultHidden column carries a persisted
 * `false` (EntityTable seeds it), so `cvr` would otherwise stay hidden, and an
 * explicit `true` on the Amazon rate would keep it showing.
 */
const SQP_CVR_DEFAULT_COLS = ['cvr', 'total_purchase_rate']

/** v11 also RENAMES a column (#114): Listings' `__scp_purch_rate` (÷
 *  impressions) became `__scp_cvr` (÷ clicks). A saved layout keys on the old
 *  id, so without this its order, width and show/hide would silently drop. */
export const LISTINGS_TABLE_ID = 'hub.listings_merged'
export const COLUMN_RENAMES_V11: Record<string, string> = { __scp_purch_rate: '__scp_cvr' }

/**
 * v11 -> v12 (PR 11, plan I2 / I6): the Restock "All Marketplaces" table
 * (Supplier Reorder) got clear columns.
 *
 *  - Three columns were RENAMED because their meaning changed: Fam Stock and
 *    Fam On-Order now count packs of THIS item (a new formula, not a relabel),
 *    and the two order quantities collapsed into one "Order qty" keyed on the
 *    effective number (yours, else the AI's). A saved layout keys on the old
 *    ids, so without the rename their position and width would silently drop.
 *  - Three columns changed their DEFAULT visibility: Fam Stock and Lead time
 *    became visible, the AI suggestion became hidden (its number now lives in
 *    Order qty and its "why"). Every defaultHidden column carries a seeded
 *    explicit `false` (EntityTable writes it), and an explicit entry outranks
 *    the new default forever — so the entries go. The owner's own explicit
 *    `true` for Fam Stock survives the rename and is then cleared to the new
 *    default, which is also visible: nothing he chose is lost.
 *  - Seven margin columns were REMOVED (they live in the Margin column's hover
 *    breakdown now). Their saved order / visibility / width entries go too.
 */
export const RESTOCK_SR_TABLE_ID = 'restock_hub.supplier_reorder'
export const COLUMN_RENAMES_V12: Record<string, string> = {
  family_total_stock: 'family_stock_packs',
  family_on_order: 'family_on_order_packs',
  manual_order_qty: 'order_qty_effective',
}
const RESTOCK_V12_NEW_DEFAULTS = ['family_stock_packs', 'lead_days', 'ai_order_qty']
export const RESTOCK_V12_REMOVED_COLS = [
  'gross_margin_pct_blended',
  'net_margin_pct_blended',
  'net_margin_pct_30d_approx',
  'net_margin_pct_120d_approx',
  'net_margin_pct_365d_approx',
  'net_margin_pct_after_ppc_120d_approx',
  'net_margin_pct_after_ppc_365d_approx',
]

/**
 * v12 -> v13 (PR 9b, I8 / I9-3): Send qty is an input, so it moved beside the
 * product on both Restock tables (Supplier Reorder: after ASIN; the
 * per-marketplace tab: after Product). A saved order wins over the declared
 * one, so only moving that one id in the saved order delivers it -- nothing else
 * in the blob changes (visibility, widths, pins and views are kept).
 */
export const RESTOCK_MPL_TABLE_ID = 'restock_hub.marketplace_view'

/**
 * Move `id` to sit right after `afterId` in a saved order, leaving every other
 * id in its relative position. Returns the input by identity when `id` is not in
 * the order (nothing to move) or already sits there. A missing anchor puts it
 * after the first id: close to the front, never lost.
 */
export function moveAfter(order: string[], id: string, afterId: string): string[] {
  const from = order.indexOf(id)
  if (from < 0) return order
  const rest = order.filter((x) => x !== id)
  const at = rest.indexOf(afterId)
  const to = at < 0 ? Math.min(1, rest.length) : at + 1
  if (to === from && at >= 0) return order
  return [...rest.slice(0, to), id, ...rest.slice(to)]
}

/** Remove every stored trace of the given columns. Returns the input by
 *  identity when none of them is present. */
export function dropStoredColumns<L extends MigratableLayout>(layout: L, ids: readonly string[]): L {
  const gone = new Set(ids)
  const stripKeys = <V,>(m: Record<string, V> | undefined): Record<string, V> | undefined => {
    if (!m || !Object.keys(m).some((k) => gone.has(k))) return m
    const nm: Record<string, V> = {}
    for (const [k, v] of Object.entries(m)) if (!gone.has(k)) nm[k] = v
    return nm
  }
  const stripIds = (a: string[] | undefined): string[] | undefined =>
    a && a.some((id) => gone.has(id)) ? a.filter((id) => !gone.has(id)) : a
  const vis = stripKeys(layout.visibility)
  const widths = stripKeys(layout.columnWidths)
  const order = stripIds(layout.columnOrder)
  const pins = stripIds(layout.pinnedColumnIds)
  if (vis === layout.visibility && widths === layout.columnWidths
      && order === layout.columnOrder && pins === layout.pinnedColumnIds) return layout
  const out = { ...layout }
  if (vis !== undefined) out.visibility = vis
  if (widths !== undefined) out.columnWidths = widths
  if (order !== undefined) out.columnOrder = order
  if (pins !== undefined) out.pinnedColumnIds = pins
  return out
}

/** Move every stored trace of a column from its old id to its new one.
 *  Returns the input by identity when nothing matched. */
export function renameStoredColumns<L extends MigratableLayout>(
  layout: L, renames: Record<string, string>,
): L {
  let out = layout
  const remapKeys = <V,>(m: Record<string, V> | undefined): Record<string, V> | undefined => {
    if (!m || !Object.keys(renames).some((k) => k in m)) return m
    const nm: Record<string, V> = {}
    for (const [k, v] of Object.entries(m)) nm[renames[k] ?? k] = v
    return nm
  }
  const remapIds = (a: string[] | undefined): string[] | undefined =>
    a && a.some((id) => id in renames) ? a.map((id) => renames[id] ?? id) : a
  const vis = remapKeys(layout.visibility)
  const widths = remapKeys(layout.columnWidths)
  const order = remapIds(layout.columnOrder)
  const pins = remapIds(layout.pinnedColumnIds)
  if (vis !== layout.visibility || widths !== layout.columnWidths
      || order !== layout.columnOrder || pins !== layout.pinnedColumnIds) {
    out = { ...layout }
    if (vis !== undefined) out.visibility = vis
    if (widths !== undefined) out.columnWidths = widths
    if (order !== undefined) out.columnOrder = order
    if (pins !== undefined) out.pinnedColumnIds = pins
  }
  return out
}

/** A stored layout, structurally — kept local so this module is pure and
 *  testable without importing the tableLayout React module. */
export interface MigratableLayout {
  visibility?: Record<string, boolean>
  columnOrder?: string[]
  columnWidths?: Record<string, number>
  pinnedColumnIds?: string[]
}

/**
 * Apply every stored-layout migration to one table's layout.
 *
 * Pure and idempotent: running it twice is the same as running it once, and a
 * layout with nothing to migrate is returned by IDENTITY (`changed: false`), so
 * callers can skip the write-back without comparing.
 *
 * Idempotent is NOT the same as safe to run forever — after this deletes the
 * keys, the user is free to hide those columns again, and re-running the rule
 * later would delete that deliberate choice too. That is what the ledger in
 * tableLayout.tsx is for: the rule runs once per table id, then never again.
 */
export function migrateStoredLayout(
  tableId: string,
  layout: MigratableLayout | undefined,
  fromVersion = 0,
): { layout: MigratableLayout | undefined; changed: boolean } {
  if (!layout) return { layout, changed: false }

  // EACH RULE IS GATED BY ITS OWN VERSION. The ledger in tableLayout.tsx records
  // one number per table and re-enters this function whenever
  // STORED_LAYOUT_VERSION rises; before `fromVersion` existed, every bump re-ran
  // EVERY rule -- so shipping v10 (a Timeline rule) re-ran v8 on any SQP table
  // still recorded below 10 and deleted visibility choices the owner had made
  // since v8. "Once per table id" was the documented contract and was not what
  // the code did. A table the ledger has never seen passes 0 and gets them all.
  let next = layout
  let changed = false

  const dropVisibility = (cols: readonly string[]) => {
    if (!next.visibility) return
    const vis = { ...next.visibility }
    let touched = false
    for (const col of cols) {
      if (col in vis) { delete vis[col]; touched = true }
    }
    if (touched) { next = { ...next, visibility: vis }; changed = true }
  }

  // v7 -> v8: the Amazon SQP columns. Scoped to the SQP tables by the same
  // `ba.sqp` prefix uiPrefs uses -- a `total_clicks` on some other table is a
  // different column and must not be touched.
  if (fromVersion < 8 && tableId.startsWith('ba.sqp')) dropVisibility(AMAZON_SQP_COLS)

  if (TIMELINE_LAYOUT_KEYS.includes(tableId)) {
    // v8 -> v9 (mirrored late, see TIMELINE_PACKING_COLS): the packing block
    // became default-visible, and a stale explicit `false` outranks that.
    if (fromVersion < 9) dropVisibility(TIMELINE_PACKING_COLS)

    if (fromVersion < 10) {
      // v9 -> v10: ASIN became default-hidden (it lives in the expanded row
      // now, and search never used the column anyway), so its stale entry goes
      // the same way.
      dropVisibility(['asin'])
      // v9 -> v10: the declared column order changed, and a saved order wins
      // over the declared one forever -- `mergeDeclaredColumnOrder` only
      // splices in ids it has never seen, it never re-orders ids the saved
      // array already holds. Dropping it is the only way the new order
      // reaches anyone.
      if (next.columnOrder) {
        next = { ...next }
        delete next.columnOrder
        changed = true
      }
    }
  }

  // v10 -> v11 (#114): CVR per click replaces Amazon's per-search purchase rate
  // as the visible SQP rate. See SQP_CVR_DEFAULT_COLS.
  if (fromVersion < 11 && tableId.startsWith('ba.sqp')) dropVisibility(SQP_CVR_DEFAULT_COLS)
  if (fromVersion < 11 && tableId === LISTINGS_TABLE_ID) {
    const renamed = renameStoredColumns(next, COLUMN_RENAMES_V11)
    if (renamed !== next) { next = renamed; changed = true }
  }

  // v11 -> v12 (PR 11): Restock Supplier Reorder columns — rename, re-default,
  // remove. ORDER MATTERS: rename first, so the owner's explicit choice for a
  // renamed column is the entry the re-default step then clears.
  if (fromVersion < 12 && tableId === RESTOCK_SR_TABLE_ID) {
    const renamed = renameStoredColumns(next, COLUMN_RENAMES_V12)
    if (renamed !== next) { next = renamed; changed = true }
    dropVisibility(RESTOCK_V12_NEW_DEFAULTS)
    const stripped = dropStoredColumns(next, RESTOCK_V12_REMOVED_COLS)
    if (stripped !== next) { next = stripped; changed = true }
  }

  // v12 -> v13 (PR 9b): Send qty beside the product, on both Restock tables.
  if (fromVersion < 13 && next.columnOrder
      && (tableId === RESTOCK_SR_TABLE_ID || tableId === RESTOCK_MPL_TABLE_ID)) {
    const moved = tableId === RESTOCK_SR_TABLE_ID
      ? moveAfter(next.columnOrder, '__send_qty', 'asin')
      : moveAfter(next.columnOrder, 'send_qty', 'product_name')
    if (moved !== next.columnOrder) { next = { ...next, columnOrder: moved }; changed = true }
  }

  return changed ? { layout: next, changed: true } : { layout, changed: false }
}
